import React, { JSX, memo, useMemo } from 'react';
import classNames from 'classnames';

import { TGlobalContainerDesigns } from '@styles/design-system/global-components/global-components.types';
import { TGlobalContainerProps } from './global-container.types';

import styles from './global-container.module.css';

const NonMemoizedGlobalContainer = <Tag extends React.ElementType = 'div'>(
  props: TGlobalContainerProps<Tag>
): JSX.Element | null => {
  const {
    style: elementStyle,
    TagName = 'div',
    mode = TGlobalContainerDesigns.GlobalCont1,
    css,
    hoverCss,
    focusCss,
    className: elementClassName,
    ...restElementProps
  } = props;

  const cssObj = useMemo(() => {
    if (!css) return {};
    if (typeof css === 'string') {
      return Object.fromEntries(
        css
          .replace(/[\r\n]+/g, '')
          .split(';')
          .map((part) => {
            const splitPart = part.split(':');
            if (splitPart.length !== 2) return ['', ''];
            return [splitPart[0].trim(), splitPart[1].trim()];
          })
      );
    }
    return css;
  }, [css]);

  const hoverCssObj = useMemo(() => {
    if (!hoverCss) return {};
    if (typeof hoverCss === 'string') {
      return Object.fromEntries(
        hoverCss
          .replace(/[\r\n]+/g, '')
          .split(';')
          .map((part) => {
            const splitPart = part.split(':');
            if (splitPart.length !== 2) return ['', ''];
            return [splitPart[0].trim(), splitPart[1].trim()];
          })
      );
    }
    return hoverCss;
  }, [hoverCss]);

  const focusCssObj = useMemo(() => {
    if (!focusCss) return hoverCssObj;

    if (typeof focusCss === 'string') {
      return Object.fromEntries(
        focusCss
          .replace(/[\r\n]+/g, '')
          .split(';')
          .map((part) => {
            const splitPart = part.split(':');
            if (splitPart.length !== 2) return ['', ''];
            return [splitPart[0].trim(), splitPart[1].trim()];
          })
      );
    }
    return focusCss;
  }, [focusCss, hoverCssObj]);

  const style = useMemo(() => {
    return {
      ...elementStyle,
      ...Object.fromEntries(Object.entries(cssObj).map(([key, value]) => [`--global-container-${key}`, value])),
      ...Object.fromEntries(
        Object.entries(hoverCssObj).map(([key, value]) => [`--global-container-hover-${key}`, value])
      ),
      ...Object.fromEntries(
        Object.entries(focusCssObj).map(([key, value]) => [`--global-container-focus-${key}`, value])
      ),
    };
  }, [cssObj, elementStyle, focusCssObj, hoverCssObj]);

  const className = useMemo(() => {
    return classNames(
      styles['global-container'],
      mode,
      elementClassName || '',
      Object.fromEntries(Object.entries(cssObj).map(([key, value]) => [[styles[key]], value])),
      Object.fromEntries(Object.entries(hoverCssObj).map(([key, value]) => [[styles[`hover-${key}`]], value])),
      Object.fromEntries(Object.entries(focusCssObj).map(([key, value]) => [[styles[`focus-${key}`]], value]))
    );
  }, [cssObj, elementClassName, focusCssObj, hoverCssObj, mode]);

  return <TagName {...restElementProps} style={style} className={className} />;
};

const GlobalContainer = memo(NonMemoizedGlobalContainer) as typeof NonMemoizedGlobalContainer;

export default GlobalContainer;
